import { useMemo } from 'react'

import { useQuery } from 'react-query'
import { useSelector } from 'react-redux'

import { type RequestTypeEnum } from '@webapp/core/entities/internal/enums/RequestTypeEnum'
import { makeSelectUser } from '@webapp/core/stores/global/selectors'
import { LegalEntitySummaryWithBaseCurrency } from '@webapp/platform/legal-entities/entities/LegalEntitySummaryWithBaseCurrency'
import { legalEntityQueryConfig } from '@webapp/platform/legal-entities/query-configs/legalEntityQueryConfig'

interface IUseQueryRequestableLegalEntitiesOptions {
  requestType: RequestTypeEnum
  enabled?: boolean
  onError?: () => void
}

interface IUseQueryRequestableLegalEntitiesReturn {
  legalEntities: LegalEntitySummaryWithBaseCurrency[]
  defaultLegalEntity: Nullable<LegalEntitySummaryWithBaseCurrency>
  isLoading: boolean
  isError: boolean
}

/**
 * The Legal Entities the current user can raise `requestType` requests in,
 * in the backend's name order, and the one to preselect: the entity of the
 * user's home primary category when it is requestable, else the first by
 * name. A failed or disabled query reads as no entities and no default.
 */
export const useQueryRequestableLegalEntities = ({
  requestType,
  enabled = true,
  onError,
}: IUseQueryRequestableLegalEntitiesOptions): IUseQueryRequestableLegalEntitiesReturn => {
  const user = useSelector(makeSelectUser())
  const homeLegalEntityUuid = user?.department?.branch?.legal_entity?.uuid

  const { data, isLoading, isError } = useQuery({
    ...legalEntityQueryConfig.requestable(requestType),
    enabled,
    onError,
  })

  const legalEntities = useMemo(
    () =>
      (data ?? []).map(
        (legalEntity) => new LegalEntitySummaryWithBaseCurrency(legalEntity)
      ),
    [data]
  )

  const defaultLegalEntity = useMemo(
    () =>
      legalEntities.find(
        (legalEntity) => legalEntity.uuid === homeLegalEntityUuid
      ) ??
      legalEntities[0] ??
      null,
    [legalEntities, homeLegalEntityUuid]
  )

  return { legalEntities, defaultLegalEntity, isLoading, isError }
}
